/**
 * The art the Live Scan HUD draws for a Pokémon, FORM-AWARE (2026-09-28).
 *
 * WHY. The card built its picture from the national dex number alone
 * (destinyArtworkUrl), so an Alolan Marowak drew the Kanto Marowak and a
 * Galarian Stunfisk drew the Kanto Stunfisk, and setting the form on the
 * record page changed nothing: the record's formId reached the HUD facts but
 * never the URL. Every other surface (species page, My Pokémon, showcases)
 * already resolves form art through speciesFormArtCandidates; the HUD now
 * goes through the same registry.
 *
 * ONE URL, NOT A CHAIN. The native renderers fetch exactly one URL and keep
 * no fallback list, so this picks the single best candidate: the official
 * artwork render of the form when the registry knows one (the mirror serves
 * other/official-artwork/<formId>.png for every regional form checked), else
 * the form's own icon (a costume's pokeminers icon has no HQ upgrade), else
 * the base species' destiny art exactly as before.
 *
 * THE EVOLUTION KEEPS THE REGION. bestPotential names the target as a plain
 * species id. A regional form evolves into the regional form of the next
 * stage (Alolan Vulpix into Alolan Ninetales), so the source form's suffix
 * is carried onto the target when the target species has a form with that
 * suffix; otherwise the target draws its base art, as before.
 */
import { FORM_GROUPS_BY_SPECIES_ID } from "@/lib/generated/pokemonForms.generated";
import { speciesFormArtCandidates } from "./formSpriteCandidates";
import { megaGmaxSpriteCandidates } from "./megaGmaxSprites";
import { destinyArtworkUrl } from "./pokemonArtwork";

/** The registry form of `speciesId` whose id ends with `suffix`, or null. */
function formWithSuffix(speciesId: number, suffix: string): string | null {
  const group = FORM_GROUPS_BY_SPECIES_ID.get(speciesId);
  if (!group) return null;
  const exact = group.forms.find((f) => f.formId === `${group.speciesName}-${suffix}`);
  if (exact) return exact.formId;
  const loose = group.forms.find((f) => f.formId.endsWith(`-${suffix}`));
  return loose ? loose.formId : null;
}

/**
 * The form the HUD should draw for `targetSpeciesId`, given the scanned
 * Pokémon's own species and form. Same species: the form itself. A different
 * species (an evolution): the target's form with the same suffix, if any.
 */
export function hudFormIdFor(
  sourceSpeciesId: number,
  sourceFormId: string | null | undefined,
  targetSpeciesId: number,
): string | null {
  if (!sourceFormId) return null;
  if (targetSpeciesId === sourceSpeciesId) return sourceFormId;
  const group = FORM_GROUPS_BY_SPECIES_ID.get(sourceSpeciesId);
  const prefix = group ? `${group.speciesName}-` : null;
  const suffix = prefix && sourceFormId.startsWith(prefix) ? sourceFormId.slice(prefix.length) : null;
  if (!suffix) return null;
  return formWithSuffix(targetSpeciesId, suffix);
}

/**
 * One URL for the card: the form's art when known, else, for a Gigantamax
 * individual with no other form, the G-Max form's art (the Max dex's own
 * roster, lib/megaGmaxSprites; 2026-09-28 owner screenshots: a G-Max
 * Grimmsnarl drew the ordinary Grimmsnarl), else the species' destiny art.
 */
export function hudArtworkUrl(
  speciesId: number,
  formId: string | null | undefined,
  opts?: { shiny?: boolean; gender?: "male" | "female" | null; gmax?: boolean },
): string {
  let candidates = speciesFormArtCandidates(speciesId, formId, { shiny: opts?.shiny });
  if ((!candidates || candidates.length === 0) && opts?.gmax) {
    const gmax = megaGmaxSpriteCandidates("gmax", speciesId, { shiny: opts?.shiny });
    if (gmax.length > 0) candidates = gmax;
  }
  if (candidates && candidates.length > 0) {
    // A form the registry keys by a PokéAPI id has an official-artwork
    // render at the second slot (formSpriteCandidates: HOME, official,
    // pixel). A costume keyed by a game icon has only the icon: use it.
    const official = candidates.find((u) => u.includes("/official-artwork/"));
    return official ?? candidates[0];
  }
  return destinyArtworkUrl(speciesId, opts);
}
